import { BadgeStatus } from '@/components/badge-order';
import { CustomerOrderDetailElementDataType } from '@/components/sections/builder/elements/body/customer/customer-order-detail/type';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import { dummyCustomerOrderDetail } from '@/constant/dummyData/dummy-customer-order';
import { cn, formatIDR, isEmpty } from '@/lib/utils';
import { StoreCustomerOrderDetailItem } from '@/types/http';
import { format } from 'date-fns';
import { ArrowLeft, Printer, Truck, Wallet } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

export default function ElementContentCustomerOrderDetail({
  element,
  data,
  preview,
}: ElementProps) {
  const dataOrderDetail = preview ? dummyCustomerOrderDetail : data;
  const content = element.data as CustomerOrderDetailElementDataType;
  const handleInvoice = () => {
    window.open(`/account/order/invoice/${dataOrderDetail?.order_id}`, '_blank');
  };

  const handlePayment = () => {
    if (!dataOrderDetail?.payment_link) return;
    window.open(dataOrderDetail?.payment_link as string);
  };

  const handleTrackOrder = () => {
    if (!dataOrderDetail?.awb_track_link) return;
    window.open(dataOrderDetail?.awb_track_link as string);
  };

  const renderButton = (status: string) => {
    const divClass =
      'flex flex-col sm:justify-end items-start w-full gap-2 font-normal sm:flex-row sm:items-center sm:gap-1';
    const buttonClass = 'w-full gap-2 px-12 py-5 sm:w-auto xl:text-lg text-base';

    switch (status) {
      case 'unpaid':
        return (
          <div className={divClass}>
            <Button type='button' variant='outline' className={buttonClass} onClick={handleInvoice}>
              <Printer className='h-5 w-5' />
              Invoice
            </Button>
            <Button
              type='button'
              className={cn(buttonClass, isEmpty(content?.colorScheme) && 'theme_button')}
              style={
                isEmpty(content?.colorScheme)
                  ? {}
                  : {
                      backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                      color: content?.colorScheme?.btnSolidlabel,
                    }
              }
              onClick={handlePayment}
            >
              <Wallet className='h-5 w-5' />
              Pay
            </Button>
          </div>
        );
      case 'processing':
      case 'ready_to_process':
      case 'paid':
        return (
          <div className={divClass}>
            <Button type='button' variant='outline' className={buttonClass} onClick={handleInvoice}>
              <Printer className='h-5 w-5' />
              Invoice
            </Button>
            <Button
              type='button'
              className={cn(buttonClass, isEmpty(content?.colorScheme) && 'theme_button')}
              style={
                isEmpty(content?.colorScheme)
                  ? {}
                  : {
                      backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                      color: content?.colorScheme?.btnSolidlabel,
                    }
              }
              onClick={handleTrackOrder}
            >
              <Truck className='h-5 w-5' />
              Track Order
            </Button>
          </div>
        );
      case 'cancel':
      case 'done':
      case 'expired':
        return (
          <div className={divClass}>
            <Button type='button' variant='outline' className={buttonClass} onClick={handleInvoice}>
              <Printer className='h-5 w-5' />
              Invoice
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className='bg_scheme_customer_order_detail customer_order_detail_padding w-full'>
        <div className='theme_layout_page_width mx-auto flex flex-col gap-3 px-6 md:px-8 xl:px-0'>
          <Link href='/account/order' className='flex items-center gap-2 text-base'>
            <ArrowLeft className='h-4 w-4' />
            Back to list
          </Link>
          <h1 className='mb-8 text-2xl font-bold'>Order Detail</h1>
          <div className='flex w-full flex-col gap-4 border-b border-[#ECEFF1] pb-5 sm:justify-between'>
            <div className='flex w-full flex-col items-start justify-between sm:flex-row sm:items-center'>
              <div className='mb-11 flex w-full flex-col items-start gap-3 sm:mb-0'>
                <h1 className='text-2xl font-bold'>#{dataOrderDetail?.order_no}</h1>
                <BadgeStatus status={dataOrderDetail?.status.toLowerCase()} />
              </div>
              {renderButton(dataOrderDetail?.status.toLowerCase())}
            </div>
          </div>

          <div className='border-b border-[#ECEFF1] pb-6'>
            <h2 className='mb-6 text-xl font-bold'>Delivery</h2>
            <div className='mb-6 grid w-full gap-6 sm:grid-cols-2'>
              <div className='flex flex-col'>
                <h3 className='text-base font-bold'>Date</h3>
                <p className='text-base'>{format(dataOrderDetail?.created_at, 'dd MMMM yyyy')}</p>
              </div>
              <div>
                <h3 className='text-base font-bold'>Address</h3>
                <p className='text-base'>{dataOrderDetail?.customer?.address || '-'}</p>
              </div>
            </div>
            <div className='grid w-full gap-6 sm:grid-cols-2'>
              <div>
                <h3 className='text-base font-bold'>Courier</h3>
                <p className='text-base'>{dataOrderDetail?.shipping?.courier_name || '-'}</p>
              </div>
              <div>
                <h3 className='text-base font-bold'>Payment Method</h3>
                <p className='text-base capitalize'>{dataOrderDetail?.payment_method || '-'}</p>
              </div>
            </div>
          </div>

          <div className='border-b border-[#ECEFF1] pb-6'>
            <h2 className='mb-6 text-xl font-bold'>
              Item ({(dataOrderDetail?.order_items && dataOrderDetail?.order_items.length) || 0})
            </h2>
            {dataOrderDetail?.order_items &&
              dataOrderDetail?.order_items.length > 0 &&
              dataOrderDetail?.order_items?.map((item: StoreCustomerOrderDetailItem) => (
                <div
                  className='mb-4 flex items-start justify-between text-[#45464E]'
                  key={item.item_id}
                >
                  <div className='flex items-center gap-4 text-sm sm:text-base'>
                    <div className='relative h-12 w-12 overflow-hidden rounded-sm border border-[#163A501F] bg-gray-50 sm:h-16 sm:w-16 sm:rounded-lg'>
                      <Image
                        src={item.image as string}
                        alt={item.item_name as string}
                        width={64}
                        height={64}
                        className='object-cover'
                      />
                    </div>
                    <div className='flex flex-col'>
                      <h3 className='font-bold'>{item?.product_name || '-'}</h3>
                      {item.product_name !== item.item_name && <span>{item.item_name}</span>}
                      <p className='text-sm'>x{item.qty}</p>
                    </div>
                  </div>
                  <div className='flex flex-col text-right text-sm sm:text-base'>
                    {(item?.discount_price as number) > 0 && (
                      <span className='whitespace-nowrap font-bold'>
                        {formatIDR(item.discount_price as number)}
                      </span>
                    )}
                    <span
                      className={cn(
                        'whitespace-nowrap',
                        item?.discount_price ? 'text-[#C2C2C2] line-through' : 'font-bold'
                      )}
                    >
                      {formatIDR(item.price)}
                    </span>
                  </div>
                </div>
              ))}
          </div>

          {/* Order Summary */}
          <div className='border-b border-[#ECEFF1] pb-6'>
            <h2 className='mb-5 text-xl font-bold'>Order Summary</h2>
            <div className='space-y-2 text-base'>
              <div className='flex justify-between'>
                <span>Subtotal</span>
                <span>{formatIDR(dataOrderDetail?.subtotal ?? 0)}</span>
              </div>
              {(dataOrderDetail?.discount_amount as number) > 0 && (
                <div className='flex justify-between'>
                  <span>Product discounts</span>
                  <span className='text-red-500'>
                    {formatIDR(dataOrderDetail?.discount_amount as number)}
                  </span>
                </div>
              )}
              <div className='flex justify-between'>
                <span>shipping cost</span>
                <span>{formatIDR(dataOrderDetail?.shipping_cost ?? 0)}</span>
              </div>
              {dataOrderDetail?.is_include_insurance && (
                <div className='flex justify-between'>
                  <span>Shipping insurance</span>
                  <span>{formatIDR(dataOrderDetail?.insurance_cost ?? 0)}</span>
                </div>
              )}
              {dataOrderDetail?.is_cod && (
                <div className='flex justify-between'>
                  <span>COD fee</span>
                  <span>{formatIDR(dataOrderDetail?.cod_fee)}</span>
                </div>
              )}
              <div className='flex justify-between'>
                <span>Platform fee</span>
                <span>{formatIDR(dataOrderDetail?.platform_fee ?? 0)}</span>
              </div>
              <div className='flex justify-between'>
                <span>Tax</span>
                <span>{formatIDR(dataOrderDetail?.total_tax ?? 0)}</span>
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between text-xl font-bold'>
            <span>Total</span>
            <span>{formatIDR(dataOrderDetail?.grand_total ?? 0)}</span>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .bg_scheme_customer_order_detail {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .customer_order_detail_padding {
            padding-top: ${content.topPadding}px;
            padding-bottom: ${content.bottomPadding}px;
          }
          .bg_scheme_customer_order_detail_detail {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
        `}
      </style>
    </>
  );
}
